import { ItemWithAvatar, VerticalFormItem } from '@components/display';
import { useAppTranslation } from '@hooks';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { LoadingOutlined } from '@ant-design/icons';
import { CertificationType } from '@pages/types';
import {
  BG_COLORS,
  Button,
  Card,
  Col,
  colors,
  Content,
  Form,
  IconSearch,
  Row,
  Space,
  TextInput,
  Text,
} from 'common-components';

import AssignManager from '../manager/assign/AssignManager';

import CreateSegmentCertification from './CreateSegmentCertification';

import { store } from '@/store';

const cardStyle = { width: '100%', padding: 24, marginBottom: 16 };

const CardHeading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Space size={8} orientation={'vertical'} style={{ width: '100%' }}>
    <Text color={colors.gray900} style={{ fontSize: 16, lineHeight: '24px', fontWeight: 500 }}>
      {title}
    </Text>
    {subtitle && <Text color={colors.gray600}>{subtitle}</Text>}
  </Space>
);

export type SettingsCertificationPropsType = {
  certification?: CertificationType
  onNextStep?: () => void
  includedFormParts?: ('settings' | 'form' | 'info' | 'audience' | 'segments' | 'managers')[]
  state?: 'creating' | 'editing'
};

const SettingsCertification = ({
  certification,
  onNextStep,
  includedFormParts = ['settings', 'audience', 'managers', 'segments'],
  state = 'creating',
}: SettingsCertificationPropsType) => {
  const { translate } = useAppTranslation();

  const addManagersToCertificationAction = store.use.addManagersToCertificationAction().call;
  const updateInformationCertificationAction = store.use.updateInformationCertificationAction().call;

  const isLoadingAddManagersToCertificationAction =
    store.use.addManagersToCertificationAction().status === 'LOADING';
  const isLoadingUpdateInformationCertificationAction =
    store.use.updateInformationCertificationAction().status === 'LOADING';
  const isLoadingUpdateNextStepCertificationAction =
    store.use.updateNextStepCertificationAction().status === 'LOADING';

  const { control, handleSubmit, getValues, reset } = useForm({
    defaultValues: {
      ...certification,
      nbAttemptsMax: 1,
    },
  });

  useEffect(() => {
    !!certification?.idCertification && reset(certification);
  }, [certification?.idCertification]);

  const onSubmit = () => {
    updateInformationCertificationAction(
      {
        idCertification: getValues().idCertification,
        nbQuestionsTest: getValues().nbQuestionsTest,
        nbAttemptsMax: getValues().nbAttemptsMax,
        icon: getValues().icon,
        name: getValues().name,
        managers: getValues().managersShort,
      },
      onNextStep,
    );
  };

  return (
    <Content>
      {includedFormParts.includes('form') && (
        <Card style={cardStyle}>
          <div style={{ marginBottom: 16 }}>
            <CardHeading
              title={translate('form.formTitle')}
              subtitle={translate('form.formSubtitle')}
            />
          </div>
          <Card style={{ width: '100%', padding: '20px 16px' }}>
            <ItemWithAvatar title={certification.formShort?.name}>
              <Text color={colors.gray600}>
                {translate(`common.states.formState.${certification.formShort?.state}`)}
              </Text>
            </ItemWithAvatar>
          </Card>
        </Card>
      )}
      <Form onFinish={handleSubmit(onSubmit)}>
        {includedFormParts.includes('settings') && (
          <Card style={cardStyle}>
            <Col span={24}>
              <div style={{ marginBottom: 22 }}>
                <CardHeading title={translate('certificationCreation.settings.settingsTitle')} />
              </div>
              {includedFormParts.includes('info') && (
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col>
                    <Controller
                      name={'icon'}
                      control={control}
                      render={({ field }) => (
                        <VerticalFormItem label={translate('createSegment.icon')}>
                          <IconSearch
                            value={field.value}
                            bgColors={BG_COLORS}
                            onSubmit={field.onChange}
                          />
                        </VerticalFormItem>
                      )}
                    />
                  </Col>
                  <Col flex={'auto'}>
                    <Controller
                      name={'name'}
                      control={control}
                      render={({ field }) => (
                        <VerticalFormItem label={translate('project.settings.labels.name')}>
                          <TextInput value={field.value} onChange={field.onChange} />
                        </VerticalFormItem>
                      )}
                    />
                  </Col>
                </Row>
              )}
              <Row gutter={22} align={'middle'} style={{ width: '100%' }}>
                <Col span={12}>
                  <Controller
                    name={'nbQuestionsTest'}
                    control={control}
                    render={({ field }) => (
                      <VerticalFormItem
                        label={translate('certificationCreation.settings.questionsPerTest')}
                      >
                        <TextInput
                          type={'number'}
                          min="0"
                          max={certification.nbQuestions}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </VerticalFormItem>
                    )}
                  />
                </Col>
                <Col span={12}>
                  <Controller
                    name={'nbAttemptsMax'}
                    defaultValue={1}
                    control={control}
                    render={({ field }) => (
                      <VerticalFormItem
                        label={translate('certificationCreation.settings.maxAttempts')}
                      >
                        <TextInput
                          type={'number'}
                          min="1"
                          value={field.value === 0 ? 1 : field.value ?? 1}
                          onChange={field.onChange}
                        />
                      </VerticalFormItem>
                    )}
                  />
                </Col>
              </Row>
            </Col>
          </Card>
        )}
        {/* HIDE
				{includedFormParts.includes("audience") && (
					<Card style={{ width: "100%", padding: 20, marginBottom: 20 }}>
						<Space
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
							}}
						>
							<Title style={{ fontSize: 18 }}>
								{translate(`certificationCreation.settings.audienceTitle`)}
							</Title>
							<Controller
								name={"audience"}
								control={control}
								render={({ field }) => (
									<CheckboxGroup value={field.value} onChange={field.onChange}>
										<Space
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-start",
											}}
										>
											<Space size={5} align={"start"}>
												<Checkbox
													key={getStateKeyByValue(
														CertificationAudienceType,
														"External",
													)}
													value={getStateKeyByValue(
														CertificationAudienceType,
														"External",
													)}
												/>
												<Space size={3} orientation={"vertical"}>
													{translate(
														`certificationCreation.settings.audience.external`,
													)}
													<Text>
														{translate(
															`certificationCreation.settings.audience.externalDesc`,
														)}
													</Text>
												</Space>
											</Space>
											<Space size={5} align={"start"}>
												<Checkbox
													key={getStateKeyByValue(
														CertificationAudienceType,
														"Candidate",
													)}
													value={getStateKeyByValue(
														CertificationAudienceType,
														"Candidate",
													)}
												/>
												<Space size={3} orientation={"vertical"}>
													{translate(
														`certificationCreation.settings.audience.candidates`,
													)}
													<Text>
														{translate(
															`certificationCreation.settings.audience.candidatesDesc`,
														)}
													</Text>
												</Space>
												<Text></Text>
											</Space>
											<Space size={5} align={"start"}>
												<Checkbox
													key={getStateKeyByValue(
														CertificationAudienceType,
														"Internal",
													)}
													value={getStateKeyByValue(
														CertificationAudienceType,
														"Internal",
													)}
												/>
												<Space size={3} orientation={"vertical"}>
													{translate(
														`certificationCreation.settings.audience.interns`,
													)}
													<Text>
														{translate(
															`certificationCreation.settings.audience.internsDesc`,
														)}
													</Text>
												</Space>
												<Text></Text>
											</Space>
										</Space>
									</CheckboxGroup>
								)}
							/>
						</Space>
					</Card>
				)}
					*/}
        {includedFormParts.includes('segments') && (
          <Card style={cardStyle}>
            <div style={{ marginBottom: 24 }}>
              <CardHeading
                title={translate('certificationCreation.settings.segments.segmentsTitle')}
                subtitle={translate('certificationCreation.settings.segments.segmentsSubtitle')}
              />
            </div>
            <CreateSegmentCertification certification={certification} />
          </Card>
        )}
        {includedFormParts.includes('managers') && (
          <Card style={cardStyle}>
            <Controller
              name={'managersShort'}
              control={control}
              render={({ field }) => {
                return (
                  <>
                    <CardHeading
                      title={translate('certificationCreation.settings.managers.managersTitle')}
                      subtitle={translate('certificationCreation.settings.managers.managersSubtitle')}
                    />
                    <AssignManager
                      modifEnabled={true}
                      isLoading={isLoadingAddManagersToCertificationAction}
                      managersUser={field.value || []}
                      onUpdateManagers={(managersShort) => {
                        field.onChange(managersShort);
                        addManagersToCertificationAction({
                          idCertification: certification.idCertification,
                          managersShort,
                        });
                      }}
                    />
                  </>
                );
              }}
            />
          </Card>
        )}
        {state === 'editing' ? (
          <Row justify={'start'} style={{ width: '100%' }}>
            <Button type={'primary'} size="sm" htmlType={'submit'}>
              {translate('project.settings.buttons.saveChanges')}
            </Button>
          </Row>
        ) : (
          <Row justify={'end'}>
            <Space size={16}>
              <Button
                size="sm"
                disabled={
                  isLoadingUpdateInformationCertificationAction ||
                  isLoadingUpdateNextStepCertificationAction ||
                  isLoadingAddManagersToCertificationAction
                }
                type={'primary'}
                onClick={handleSubmit(onSubmit)}
              >
                {isLoadingUpdateNextStepCertificationAction ||
                isLoadingUpdateInformationCertificationAction ? (
                    <LoadingOutlined />
                  ) : (
                    translate('certificationCreation.finishButton')
                  )}
              </Button>
            </Space>
          </Row>
        )}
      </Form>
    </Content>
  );
};

export default SettingsCertification;
